import { useState } from "react";
import {
  Card,
  Group,
  Text,
  Button,
  Badge,
  Select,
  ActionIcon,
  Menu,
  Code,
} from "@mantine/core";
import {
  IconPlus,
  IconDotsVertical,
  IconEdit,
  IconBan,
  IconRotate,
} from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { modals } from "@mantine/modals";
import { payoutCatalogueApi } from "../../api/payoutCatalogue";
import type {
  PayoutDestinationCategory,
  SelcomCatalogueEntry,
} from "../../types/domain";
import { usePaginatedList } from "../../hooks/usePaginatedList";
import { PageHeader } from "../../components/common/PageHeader";
import { SearchInput } from "../../components/common/SearchInput";
import { DataTable, type Column } from "../../components/common/DataTable";
import { errorMessage } from "../../lib/axios";
import {
  PayoutCatalogueFormModal,
  type PayoutCatalogueFormValues,
} from "./PayoutCatalogueFormModal";

const CATEGORY_LABEL: Record<PayoutDestinationCategory, string> = {
  bank: "Bank",
  "mobile-money": "Mobile Money",
  "selcom-pesa": "Selcom Pesa",
};

const CATEGORY_FILTER_OPTIONS = [
  { value: "", label: "All categories" },
  { value: "bank", label: "Bank" },
  { value: "mobile-money", label: "Mobile Money" },
  { value: "selcom-pesa", label: "Selcom Pesa" },
];

export function PayoutCataloguePage() {
  const qc = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<SelcomCatalogueEntry | null>(null);
  const [category, setCategory] = useState<string>("");

  const { page, setPage, search, onSearchChange, result, isLoading, error } =
    usePaginatedList<SelcomCatalogueEntry>({
      queryKey: ["payout-catalogue"],
      fetcher: payoutCatalogueApi.list,
      limit: 50,
      filters: {
        category: category || undefined,
      },
    });

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ["payout-catalogue"] });
  }

  const saveMutation = useMutation({
    mutationFn: (values: PayoutCatalogueFormValues) =>
      editing
        ? payoutCatalogueApi.update(editing._id, {
            name: values.name,
            referenceType: values.referenceType,
            lookupEnabled: values.lookupEnabled,
            isActive: values.isActive,
          })
        : payoutCatalogueApi.create(values),
    onSuccess: () => {
      notifications.show({
        color: "green",
        message: editing ? "Destination updated" : "Destination created",
      });
      setFormOpen(false);
      setEditing(null);
      invalidate();
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => payoutCatalogueApi.remove(id),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Destination deactivated" });
      invalidate();
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => payoutCatalogueApi.update(id, { isActive: true }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Destination reactivated" });
      invalidate();
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  function confirmDeactivate(entry: SelcomCatalogueEntry) {
    modals.openConfirmModal({
      title: "Deactivate payout destination",
      children: (
        <Text size="sm">
          Deactivate <b>{entry.name}</b> ({entry.shortcode})? It will be hidden
          from the driver app's destination picker. Drivers who already saved
          a destination using it keep it — this doesn't delete anything.
        </Text>
      ),
      labels: { confirm: "Deactivate", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: () => deactivateMutation.mutate(entry._id),
    });
  }

  const columns: Column<SelcomCatalogueEntry>[] = [
    {
      key: "name",
      header: "Name",
      render: (e) => (
        <Text size="sm" fw={600}>
          {e.name}
        </Text>
      ),
    },
    {
      key: "shortcode",
      header: "Shortcode",
      render: (e) => <Code>{e.shortcode}</Code>,
    },
    {
      key: "category",
      header: "Category",
      render: (e) => (
        <Badge variant="light">
          {CATEGORY_LABEL[e.category] ?? e.category}
        </Badge>
      ),
    },
    {
      key: "referenceType",
      header: "Reference format",
      render: (e) => (
        <Text size="sm" c="dimmed">
          {e.referenceType === "ALPHANUMERIC" ? "Alphanumeric" : "Numeric"}
        </Text>
      ),
    },
    {
      key: "lookup",
      header: "Lookup",
      render: (e) => (
        <Badge
          size="sm"
          variant="light"
          color={e.lookupEnabled ? "green" : "gray"}
        >
          {e.lookupEnabled ? "Verified" : "Unverified"}
        </Badge>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (e) => (
        <Badge size="sm" variant="light" color={e.isActive ? "green" : "gray"}>
          {e.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (e) => (
        <Menu position="bottom-end" withArrow>
          <Menu.Target>
            <ActionIcon variant="subtle" color="gray">
              <IconDotsVertical size={18} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconEdit size={16} />}
              onClick={() => {
                setEditing(e);
                setFormOpen(true);
              }}
            >
              Edit
            </Menu.Item>
            {e.isActive ? (
              <Menu.Item
                color="red"
                leftSection={<IconBan size={16} />}
                onClick={() => confirmDeactivate(e)}
              >
                Deactivate
              </Menu.Item>
            ) : (
              <Menu.Item
                leftSection={<IconRotate size={16} />}
                onClick={() => reactivateMutation.mutate(e._id)}
              >
                Reactivate
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Payout Destinations"
        subtitle="Banks, mobile money and Selcom Pesa drivers can cash out to"
        actions={
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            New destination
          </Button>
        }
      />
      <Card withBorder radius="md" padding="lg">
        <Group mb="md" gap="sm">
          <SearchInput
            value={search}
            onChange={onSearchChange}
            placeholder="Search name or shortcode…"
          />
          <Select
            data={CATEGORY_FILTER_OPTIONS}
            value={category}
            onChange={(val) => setCategory(val ?? "")}
            w={180}
            allowDeselect={false}
          />
        </Group>
        <DataTable
          columns={columns}
          rows={result?.items ?? []}
          rowKey={(e) => e._id}
          loading={isLoading}
          error={error ? errorMessage(error) : null}
          emptyLabel="No payout destinations yet"
          page={page}
          totalPages={result?.totalPages}
          total={result?.total}
          onPageChange={setPage}
        />
      </Card>

      <PayoutCatalogueFormModal
        opened={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        editing={editing}
        loading={saveMutation.isPending}
        onSubmit={(values) => saveMutation.mutate(values)}
      />
    </>
  );
}
